/**
 * The seven admin power actions and how each one is presented.
 *
 * Deliberately NOT a port of the client app's `lib/resourcePowerActions.ts` any more: that file
 * mirrors the self-service backend guards (kind, expiry, per-action status) so an action is offered
 * exactly when the request behind it would succeed, and the client keeps that behaviour. The admin
 * endpoints run no status or expiry gate at all, so there is nothing here to mirror — every action
 * is offered on every VM in every state, and the hypervisor is what refuses the ones it cannot
 * take. Hiding them was the old way of saying "this would fail"; now the failure comes back from
 * the provider with its own reason, which is what an operator repairing a stuck row needs to read.
 */

/** Route segment and command value for the seven admin power endpoints. */
export const RESOURCE_POWER_ACTIONS = [
  'start',
  'stop',
  'shutdown',
  'restart',
  'reset',
  'suspend',
  'resume',
] as const

export type ResourcePowerAction = (typeof RESOURCE_POWER_ACTIONS)[number]

/** How one power action is labelled, and whether it needs a confirmation first. */
export interface ResourcePowerActionMeta {
  label: string
  /** Bypasses the guest OS, so the operator is asked before it runs. */
  destructive: boolean
  /** Confirmation title, present only for destructive actions. */
  confirmTitle?: string
  /** Confirmation body, `{name}` replaced with the resource name. */
  confirmDescription?: string
}

export const RESOURCE_POWER_ACTION_META: Record<
  ResourcePowerAction,
  ResourcePowerActionMeta
> = {
  start: { label: 'Start', destructive: false },
  shutdown: { label: 'Shutdown', destructive: false },
  stop: {
    // Labels are the bare action the operator is choosing, so they read the same way down the
    // menu: Start, Shutdown, Stop, Reboot, Reset, Suspend, Resume. What makes this one different
    // from Shutdown - no word to the guest OS - belongs in the confirmation below, where it is
    // read at the moment it matters, and in the destructive colour the item is rendered in.
    label: 'Stop',
    destructive: true,
    confirmTitle: 'Stop this VM?',
    confirmDescription:
      'Power to "{name}" is cut without telling the guest OS, like pulling the plug. Unsaved work in the guest is lost.',
  },
  restart: { label: 'Reboot', destructive: false },
  reset: {
    label: 'Reset',
    destructive: true,
    confirmTitle: 'Reset this VM?',
    confirmDescription:
      '"{name}" is power-cycled without telling the guest OS. Unsaved work in the guest is lost.',
  },
  suspend: { label: 'Suspend', destructive: false },
  resume: { label: 'Resume', destructive: false },
}
